import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import type { IToolDetails } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { getComposioConfig } from './tool-detail-utils';

const PAGE_SIZE = 20;

export function ComposioConfiguration({ tool }: { tool: IToolDetails }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { toolkits, tools, limit } = getComposioConfig(tool);

  const filteredSlugs = useMemo(() => {
    const slugs = tools ?? [];
    const term = search.trim().toLowerCase();
    return term ? slugs.filter((slug) => slug.toLowerCase().includes(term)) : slugs;
  }, [tools, search]);

  const pageCount = Math.max(1, Math.ceil(filteredSlugs.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const paginatedSlugs = filteredSlugs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  if (toolkits.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-sm text-muted-foreground">
            {t('NO_TOOLKITS_CONFIGURED') ||
              'No toolkits configured yet. Edit this tool to add toolkits like OUTLOOK or MICROSOFT_TEAMS.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('TOOLKITS') || 'Toolkits'}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {toolkits.map((toolkit) => (
            <Badge key={toolkit} variant="outline">
              {toolkit}
            </Badge>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('ACTION_ALLOWLIST') || 'Action allowlist'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {tools && tools.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground">
                {t('COMPOSIO_ALLOWLIST_SUMMARY', {
                  defaultValue: 'The agent may use exactly these {{count}} action(s).',
                  count: tools.length,
                })}
              </p>
              <Input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder={t('SEARCH_ACTIONS') || 'Search actions...'}
                className="h-8 max-w-xs text-[13px]"
              />
              <div className="flex flex-wrap gap-1.5">
                {paginatedSlugs.map((slug) => (
                  <Badge key={slug} variant="secondary" className="break-all font-mono text-xs">
                    {slug}
                  </Badge>
                ))}
                {paginatedSlugs.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    {t('NO_ACTIONS_FOUND') || 'No actions found.'}
                  </p>
                )}
              </div>
              {pageCount > 1 && (
                <div className="flex items-center gap-3 text-sm">
                  <button
                    type="button"
                    className="text-primary disabled:text-muted-foreground"
                    disabled={currentPage <= 1}
                    onClick={() => setPage(currentPage - 1)}
                  >
                    {t('PREVIOUS') || 'Previous'}
                  </button>
                  <span className="text-muted-foreground">
                    {currentPage} / {pageCount}
                  </span>
                  <button
                    type="button"
                    className="text-primary disabled:text-muted-foreground"
                    disabled={currentPage >= pageCount}
                    onClick={() => setPage(currentPage + 1)}
                  >
                    {t('NEXT') || 'Next'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('COMPOSIO_NO_ALLOWLIST', {
                defaultValue:
                  'No explicit allowlist — the top {{limit}} actions per toolkit are used. Edit the tool to pin exact actions (recommended for critical actions like sending email).',
                limit: limit ?? 10,
              })}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
